import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, UserMinus, Calendar } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/combineReducer';
import { isFranchiseTenant } from '../../utils/franchise/franchiseAbsenteeismMenu';
import './stats.scss';

// Interfaces para los datos de estadísticas
interface TurnosPorMes {
  month: string;
  total: number;
}

// interface TotalCandidatos {
//   type: string;
//   count: number;
//   month?: string;
// }

interface MayorAusentismo {
  employee_id: string;
  name: string;
  dni: string;
  total_absences: number;
}

interface AusentismosDelMes {
  visita_domicilio: number;
  videollamada: number;
  control_consultorio: number;
  total_count: number;
}

interface StatsData {
  turnosPorMes: TurnosPorMes[];
  // totalCandidatos: TotalCandidatos[];
  mayorAusentismo: MayorAusentismo | null;
  ausentismosDelMes: AusentismosDelMes;
}

interface StatisticsDashboardProps {
  statsData?: StatsData;
  loading?: boolean;
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  delay: number;
  trend?: 'up' | 'down' | 'neutral';
  details?: string[];
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  delay,
  trend,
  details
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const getTrendColor = () => {
    if (trend === 'up') return '#28a745';
    if (trend === 'down') return '#dc3545';
    return '#6c757d';
  };

  return (
    <div
      className={`stat-card ${isVisible ? 'visible' : ''}`}
      style={{ '--delay': `${delay}ms`, '--color': color } as React.CSSProperties}
      onClick={() => details && setShowDetails(!showDetails)}
    >
      <div className="card h-100 border-0 shadow-sm">
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="stat-icon">
              {icon}
            </div>
            {trend && (
              <div className="trend-indicator" style={{ color: getTrendColor() }}>
                <TrendingUp size={20} />
              </div>
            )}
          </div>

          <div className="stat-content">
            <h3 className="stat-title mb-2">{title}</h3>
            <div className="stat-value mb-1">{value}</div>
            {subtitle && <div className="stat-subtitle">{subtitle}</div>}
          </div>

          {details && (
            <div className={`details-section mt-3 ${showDetails ? 'expanded' : ''}`}>
              <div className="details-content">
                {details.map((detail, index) => (
                  <div key={index} className="detail-item">
                    <small className="text-muted">{detail}</small>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card-footer bg-transparent border-0 pt-0">
          {details && (
            <small className="text-muted">
              {showDetails ? 'Ocultar detalles' : 'Ver detalles'} ▼
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

const StatisticsDashboard: React.FC<StatisticsDashboardProps> = ({
  statsData,
  loading = false
}) => {
  const franchiseState = useSelector((state: RootState) => state.franchise);
  const hideAbsenteeism = isFranchiseTenant(franchiseState);

  // Data por defecto si no se proporciona
  const defaultData: StatsData = {
    turnosPorMes: [],
    // totalCandidatos: [{ type: "total", count: 0 }],
    mayorAusentismo: null,
    ausentismosDelMes: {
      visita_domicilio: 0,
      videollamada: 0,
      control_consultorio: 0,
      total_count: 0
    }
  };

  const data = statsData || defaultData;

  const getCurrentMonthYear = () => {
    const currentDate = new Date();
    const monthName = currentDate.toLocaleDateString('es-ES', { month: 'long' });
    const year = currentDate.getFullYear();
    return `${monthName.toUpperCase()} ${year}`;
  };

  const getCurrentMonthTurnos = () => {
    if (!data.turnosPorMes.length) return 0;
    const currentDate = new Date();
    const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    const currentMonthData = data.turnosPorMes.find(item => item.month === currentMonth);
    return currentMonthData?.total || 0;
  };

  const getAbsenteeDetails = () => {
    if (!data.mayorAusentismo) return [];
    return [`${data.mayorAusentismo.name}: ${data.mayorAusentismo.total_absences} ausencias`];
  };

  const getAusentismosDetails = () => {
    const ausentismos = data.ausentismosDelMes;
    return [
      `Visita a domicilio: ${ausentismos.visita_domicilio}`,
      `Videollamada: ${ausentismos.videollamada}`,
      `Control en consultorio: ${ausentismos.control_consultorio}`
    ];
  };

  const getMonthlyTrendsDetails = () => {
    if (!data.turnosPorMes.length) return [];
    return data.turnosPorMes.map(item => {
      const monthName = new Date(item.month + '-01').toLocaleDateString('es-ES', {
        month: 'long',
        year: 'numeric'
      });
      return `${monthName}: ${item.total} turnos`;
    });
  };

  // const getTotalCandidatos = () => {
  //   const totalData = data.totalCandidatos.find(item => item.type === "total");
  //   return totalData?.count || 0;
  // };

  // const getCurrentMonthCandidatos = () => {
  //   const currentMonthData = data.totalCandidatos.find(item => item.type === "current_month");
  //   return currentMonthData?.count || 0;
  // };

  if (loading) {
    return (
      <div className="statistics-dashboard">
        <div className="container-fluid py-4">
          <div className="text-center mb-4 text-red-500 animate-pulse">Recolectando datos...</div>
          <div className="row g-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="col-lg-3 col-md-6">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="skeleton skeleton-icon"></div>
                      <div className="skeleton skeleton-trend"></div>
                    </div>
                    <div className="skeleton skeleton-title mb-2"></div>
                    <div className="skeleton skeleton-value mb-1"></div>
                    <div className="skeleton skeleton-subtitle"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="statistics-dashboard">
      <div className="container-fluid py-4">
        <div className="row g-4">
          <div className="col-lg-3 col-md-6">
            <StatCard
              title={`TURNOS ${getCurrentMonthYear()}`}
              value={getCurrentMonthTurnos()}
              subtitle={new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
              icon={<Calendar size={32} />}
              color="#007bff"
              delay={0}
              trend="neutral"
              details={getMonthlyTrendsDetails()}
            />
          </div>

          {/* `          <div className="col-lg-3 col-md-6">
            <StatCard
              title="Total Candidatos"
              value={getTotalCandidatos().toLocaleString()}
              subtitle={`${getCurrentMonthCandidatos()} este mes`}
              icon={<Users size={32} />}
              color="#28a745"
              delay={200}
              trend="up"
            />
          </div>` */}

          {!hideAbsenteeism && (
            <>
              <div className="col-lg-3 col-md-6">
                <StatCard
                  title={`AUSENTISMOS ${getCurrentMonthYear()}`}
                  value={data.ausentismosDelMes.total_count}
                  subtitle={new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                  icon={<UserMinus size={32} />}
                  color="#ffc107"
                  delay={400}
                  trend="neutral"
                  details={getAusentismosDetails()}
                />
              </div>

              <div className="col-lg-3 col-md-6">
                <StatCard
                  title={`MAYOR AUSENTISMO ${getCurrentMonthYear()}`}
                  value={data.mayorAusentismo?.total_absences || 0}
                  subtitle={data.mayorAusentismo?.name.split(',')[0] || 'Sin datos'}
                  icon={<TrendingUp size={32} />}
                  color="#dc3545"
                  delay={600}
                  trend="down"
                  details={getAbsenteeDetails()}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatisticsDashboard;